'use client';

import { useState, useEffect } from 'react';
import {
  Calendar,
  ChevronLeft,
  MapPin,
  Clock,
  BookOpenText,
  Radio,
  Baby,
  Hand,
  Coffee,
  Droplet,
  CalendarCheck,
  type LucideIcon,
} from 'lucide-react';
import {
  ResponsiveSheet,
  SheetPage,
  useResponsiveSheet,
} from '@church/nextjs-ui/components/ResponsiveSheet';
import type { SermonSeries, Sermon, ServiceTime } from '@/types/serve-finder';
import { Img, useAssetUrl } from '@/lib/asset-url';
import { BaseCard } from './BaseCard';
import { CardCTA } from './CardCTA';
import { SermonDetailContent, SermonList } from './SermonDetailContent';

const BADGE_ICON_MAP: Record<string, LucideIcon> = {
  droplet: Droplet,
  'map-pin': MapPin,
  clock: Clock,
  'calendar-check': CalendarCheck,
};

interface CurrentSeriesCardProps {
  series: SermonSeries;
}

interface SermonSlide {
  label: string;
  icon: React.FC<{ className?: string }>;
  bg: string;
  bgImage?: string;
  bgImageOpacity?: number;
  headingColor: string;
  textColor: string;
  subtitleColor: string;
  align: 'left' | 'right' | 'center';
  hideLabel?: boolean;
  render: () => React.ReactNode;
}

export function CurrentSeriesCard({ series }: CurrentSeriesCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSermon, setSelectedSermon] = useState<Sermon | null>(null);
  const [initialPage, setInitialPage] = useState<'main' | 'sermon-detail'>('main');
  const resolve = useAssetUrl();

  const bgStyle = (url: string): React.CSSProperties => ({
    backgroundImage: `url(${resolve(url)})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  });

  const currentSermon = series.Sermons.find((s) => s.Is_Current) ?? series.Sermons[0];

  const openSermonDetail = () => {
    setSelectedSermon(currentSermon);
    setInitialPage('sermon-detail');
    setIsOpen(true);
  };

  const openSeriesList = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedSermon(null);
    setInitialPage('main');
    setIsOpen(true);
  };

  const BADGE_IMAGE_MAP: Record<string, string> = {
    Baptisms: '/Sermon-Series/baptisms.jpg',
  };

  // Build cycling slides from current sermon data
  const slides: SermonSlide[] = [];

  if (currentSermon) {
    slides.push({
      label: 'Speaker',
      icon: BookOpenText,
      bg: '#39AEDA',
      bgImage: '/Sermon-Series/SlideBGs/2.png',
      bgImageOpacity: 0.2,
      headingColor: '#ffffff',
      textColor: '#ffffff',
      subtitleColor: 'rgba(255,255,255,0.6)',
      align: 'left',
      hideLabel: true,
      render: () => (
        <div className="relative flex w-full" style={{ minHeight: 90 }}>
          <div className="relative z-10 flex flex-1 flex-col justify-center px-1 py-2">
            <p className="text-[10px] font-semibold tracking-[0.2em] text-white/50 uppercase">
              Speaker
            </p>
            {currentSermon.Speaker && (
              <p className="mt-1 text-2xl leading-[0.9] font-black text-white uppercase">
                {currentSermon.Speaker}
              </p>
            )}
          </div>
          {currentSermon.Speaker_Image_URL && (
            <div className="absolute top-3 right-2 bottom-3 flex w-[25%] items-center justify-end">
              <Img
                src={currentSermon.Speaker_Image_URL}
                alt={currentSermon.Speaker ?? ''}
                className="h-full max-h-16 w-auto object-cover"
                style={{ aspectRatio: '1', borderRadius: '50%' }}
              />
            </div>
          )}
        </div>
      ),
    });

    if (currentSermon.Service_Times && currentSermon.Service_Times.length > 0) {
      const grouped = currentSermon.Service_Times.reduce<Record<string, ServiceTime[]>>(
        (acc, t) => {
          (acc[t.Day] ??= []).push(t);
          return acc;
        },
        {}
      );
      slides.push({
        label: 'Service Times',
        icon: Clock,
        bg: '#E65E5B',
        bgImage: '/Sermon-Series/SlideBGs/1.png',
        bgImageOpacity: 0.2,
        headingColor: '#F3D54B',
        textColor: '#ffffff',
        subtitleColor: 'rgba(255,255,255,0.6)',
        align: 'center',
        hideLabel: true,
        render: () => (
          <div className="space-y-2">
            {Object.entries(grouped).map(([day, dayTimes]) => (
              <div key={day}>
                <p
                  className="mb-0.5 text-xs font-bold tracking-[0.2em] uppercase"
                  style={{ color: 'var(--slide-heading)' }}
                >
                  {day}
                </p>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-0.5">
                  {dayTimes.map((t) => (
                    <span
                      key={`${day}-${t.Time}`}
                      className="text-3xl font-black"
                      style={{ color: 'var(--slide-text)' }}
                    >
                      {t.Time}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ),
      });
    }

    if (currentSermon.Location) {
      const locationName = currentSermon.Location.split('\n')[0];
      slides.push({
        label: 'Location',
        icon: MapPin,
        bg: '#39AEDA',
        bgImage: '/Sermon-Series/SlideBGs/2.png',
        bgImageOpacity: 0.2,
        headingColor: '#ffffff',
        textColor: '#ffffff',
        subtitleColor: 'rgba(255,255,255,0.6)',
        align: 'left',
        hideLabel: true,
        render: () => (
          <div className="relative flex w-full" style={{ minHeight: 90 }}>
            <div className="relative z-10 flex flex-1 flex-col justify-center px-1 py-2">
              <p
                className="text-[10px] font-semibold tracking-[0.2em] uppercase"
                style={{ color: 'var(--slide-subtitle)' }}
              >
                Location
              </p>
              <p className="mt-1 text-xl leading-[0.95] font-black text-white uppercase">
                {locationName}
              </p>
            </div>
            <div className="absolute top-3 right-2 bottom-3 flex w-[25%] items-center justify-end">
              <Img
                src="/Sermon-Series/map.png"
                alt=""
                className="h-full w-auto object-cover"
                style={{ aspectRatio: '1', borderRadius: '50%' }}
              />
            </div>
          </div>
        ),
      });
    }

    for (const badge of currentSermon.Badges.filter((b) => b.Label !== 'Church in the Park')) {
      const BIcon = BADGE_ICON_MAP[badge.Icon] ?? CalendarCheck;
      const fgImage = BADGE_IMAGE_MAP[badge.Label];
      slides.push({
        label: badge.Label,
        icon: BIcon,
        bg: '#E65E5B',
        bgImage: '/Sermon-Series/SlideBGs/1.png',
        bgImageOpacity: 0.2,
        headingColor: '#F3D54B',
        textColor: '#ffffff',
        subtitleColor: 'rgba(255,255,255,0.6)',
        align: 'right',
        hideLabel: true,
        render: () => (
          <div className="relative flex w-full" style={{ minHeight: 90 }}>
            {fgImage && (
              <div className="absolute top-3 bottom-3 left-2 flex w-[25%] items-center justify-start">
                <Img
                  src={fgImage}
                  alt=""
                  className="h-full w-auto object-cover"
                  style={{ aspectRatio: '1', borderRadius: '50%' }}
                />
              </div>
            )}
            <div className="relative z-10 flex flex-1 flex-col items-end justify-center px-1 py-2">
              <p
                className="text-right text-3xl leading-none font-black uppercase"
                style={{ color: 'var(--slide-heading)' }}
              >
                {badge.Label}
              </p>
            </div>
          </div>
        ),
      });
    }
  }

  const [slideIndex, setSlideIndex] = useState(0);
  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [slideIndex, slides.length]);

  const activeIndex = slides.length > 0 ? slideIndex % slides.length : 0;
  const activeSlide = slides[activeIndex];

  return (
    <>
      <BaseCard
        variant="standard"
        as="div"
        className="cursor-pointer"
        style={bgStyle(series.Vertical_BG_URL)}
        onClick={openSermonDetail}
        header={
          <div className="relative aspect-video w-full">
            {currentSermon?.Is_Current && (
              <div
                className="absolute top-3 left-3 z-20 flex h-5 w-5 items-center justify-center bg-red-500"
                style={{ borderRadius: '50%' }}
              >
                <Radio className="h-3 w-3 animate-pulse text-white" />
              </div>
            )}

            {slides.length > 1 && (
              <div className="absolute top-3 right-3 z-20 flex items-center gap-1">
                {slides.map((_, i) => (
                  <div
                    key={i}
                    className="h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: i === activeIndex ? '16px' : '6px',
                      backgroundColor: i === activeIndex ? '#E95D5A' : 'rgba(255,255,255,0.4)',
                    }}
                  />
                ))}
              </div>
            )}

            <div className="absolute inset-0 flex items-center justify-center px-6">
              {series.FG_URL && (
                <Img
                  src={series.FG_URL}
                  alt={series.Title}
                  className="h-auto max-h-[60%] max-w-[80%] object-contain drop-shadow-lg"
                />
              )}
            </div>
            {currentSermon && (
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 px-4 pb-2">
                <Calendar className="h-3 w-3 shrink-0 text-gray-500" />
                <span className="text-[11px] font-semibold tracking-widest text-gray-500 uppercase">
                  {currentSermon.Date}
                </span>
                <span className="truncate text-[11px] font-bold tracking-wide text-gray-500 uppercase">
                  {currentSermon.Title}
                </span>
              </div>
            )}
          </div>
        }
        body={
          slides.length > 0 && activeSlide ? (
            <div className="flex h-full items-stretch px-4 py-2">
              <div
                className="relative w-full overflow-hidden px-3 py-2 transition-colors duration-500"
                style={{ backgroundColor: activeSlide.bg }}
              >
                {activeSlide.bgImage && (
                  <Img
                    src={activeSlide.bgImage}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
                    style={{ opacity: activeSlide.bgImageOpacity ?? 1 }}
                  />
                )}

                <div className="relative z-10 grid h-full" style={{ gridTemplate: '1fr / 1fr' }}>
                  {slides.map((s, i) => {
                    const isActive = i === activeIndex;
                    const textAlign =
                      s.align === 'center'
                        ? 'text-center items-center'
                        : s.align === 'right'
                          ? 'text-right items-end'
                          : 'text-left items-start';
                    return (
                      <div
                        key={i}
                        className={`flex flex-col justify-center ${textAlign}`}
                        style={
                          {
                            gridArea: '1 / 1',
                            opacity: isActive ? 1 : 0,
                            transition: 'opacity 0.4s ease-in-out',
                            pointerEvents: isActive ? 'auto' : 'none',
                            '--slide-heading': s.headingColor,
                            '--slide-text': s.textColor,
                            '--slide-subtitle': s.subtitleColor,
                          } as React.CSSProperties
                        }
                        aria-hidden={!isActive}
                      >
                        {!s.hideLabel && (
                          <h3
                            className="mb-1 text-sm leading-tight font-black tracking-wide uppercase"
                            style={{ color: s.headingColor }}
                          >
                            {s.label}
                          </h3>
                        )}
                        {s.render()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div />
          )
        }
        footer={
          <CardCTA
            label="View Series"
            onClick={openSeriesList}
            customIcon={<BookOpenText className="h-3.5 w-3.5 text-gray-600" />}
            className="border-black/5 text-gray-600 hover:text-gray-900"
          />
        }
      />

      <ResponsiveSheet
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSelectedSermon(null);
        }}
        maxWidth="max-w-3xl"
        noPanelPadding
        panelClassName=""
        panelStyle={bgStyle(series.Vertical_BG_URL)}
        defaultPage={initialPage}
        header={
          <div className="relative flex items-center justify-center p-6">
            {currentSermon?.Is_Current && (
              <div
                className="absolute top-3 left-3 flex h-6 w-6 items-center justify-center bg-red-500 shadow-md"
                style={{ borderRadius: '50%' }}
              >
                <Radio className="h-3.5 w-3.5 animate-pulse text-white" />
              </div>
            )}
            {series.FG_URL && (
              <Img
                src={series.FG_URL}
                alt={series.Title}
                className="h-auto max-h-[90px] w-2/5 object-contain drop-shadow-lg sm:max-h-[110px]"
              />
            )}
          </div>
        }
      >
        <SheetPage name="main">
          <SheetSermonList
            sermons={series.Sermons}
            isOpen={isOpen}
            onSelectSermon={setSelectedSermon}
            pageUrl={series.Page_URL}
          />
        </SheetPage>

        <SheetPage name="sermon-detail">
          {selectedSermon && (
            <SermonSheetDetail
              sermon={selectedSermon}
              index={series.Sermons.findIndex((s) => s.ID === selectedSermon.ID) + 1}
              pageUrl={series.Page_URL}
            />
          )}
        </SheetPage>
      </ResponsiveSheet>
    </>
  );
}

// Sheet-specific list wrapper — handles navigation on sermon select
function SheetSermonList({
  sermons,
  isOpen,
  onSelectSermon,
  pageUrl,
}: {
  sermons: Sermon[];
  isOpen: boolean;
  onSelectSermon: (sermon: Sermon) => void;
  pageUrl?: string | null;
}) {
  const { navigate } = useResponsiveSheet();

  return (
    <SermonList
      sermons={sermons}
      isOpen={isOpen}
      onSelectSermon={(sermon) => {
        onSelectSermon(sermon);
        navigate('sermon-detail');
      }}
      pageUrl={pageUrl}
    />
  );
}

// Sheet-specific wrapper that adds the back button above the shared detail content
function SermonSheetDetail({
  sermon,
  index,
  pageUrl,
}: {
  sermon: Sermon;
  index: number;
  pageUrl?: string | null;
}) {
  const { goBack } = useResponsiveSheet();

  return (
    <div className="relative space-y-5 p-5">
      <button
        type="button"
        onClick={goBack}
        className="relative flex items-center gap-1 text-xs font-semibold tracking-wide text-gray-500 uppercase transition-colors hover:text-gray-900"
      >
        <ChevronLeft className="h-4 w-4" />
        All Sermons
      </button>
      <SermonDetailContent sermon={sermon} index={index} pageUrl={pageUrl} />
    </div>
  );
}
